<script setup>
import {onBeforeMount, onMounted, reactive, ref} from 'vue'
import {clone, Feedback, Progress, State, can, go, titlecase} from '@/paks/vu-app'
import {getDisplay, getView, getViewChildren, persistDisplay, updateDisplay} from './BoardUtils.js'
import PageEdit from './PageEdit.vue'
import SelectDisplay from './SelectDisplay.vue'

const Fields = [
    {
        name: 'drag',
        title: 'Order',
        icon: (v, rec) => (rec.path != '/' ? '$drag' : ''),
        cursor: (v, rec) => (rec.path != '/' ? 'grab' : 'default'),
        width: '5%',
    },
    {name: 'edit', icon: '$edit', width: '5%'},
    // {name: 'index'},
    {
        name: 'design',
        icon: (v, rec) => (rec.component == 'Home' || rec.component == 'Page' ? '$dashboard' : ''),
        width: '5%',
    },
    {name: 'show', icon: '$launch', width: '5%'},
    {name: 'icon', icon: (v, rec) => (rec.icon ? rec.icon : ''), desktop: true},
    {
        name: 'tab',
        title: 'Tab Group',
        icon: (v, rec) => (Array.isArray(rec.tabs) && rec.tabs.length == 0 ? '$check' : ''),
        width: '5%',
        desktop: true,
    },
    {name: 'path'},
    {name: 'role', desktop: true},
    {name: 'menu', desktop: true},
    {name: 'component', desktop: true},
    {name: 'children', format: (v, rec) => getViewChildren(rec.views).length, desktop: true},
]

const page = reactive({
    builder: State.config.builder,
    designPageEdit: false,
    display: {},
    displays: [],
    displayName: null,
    drag: null,
    deploying: false,
    fields: Fields,
    showNested: State.app.showNestedPages || false,
    showPageEdit: false,
    view: null,
    views: null,
})

const table = ref(null)
const confirm = ref(null)

onBeforeMount(() => {
    page.drag = {
        vertical: {dir: 'vertical', move: true, removeOnSpill: false, accepts: accepts},
    }
})

onMounted(async () => {
    page.select = {
        actions: {Add: 0, Edit: 1, Delete: 2},
        multi: true,
    }
    page.displayName = State.app.display.name
    page.displays = State.app.fullDisplay.displays || []
    page.display = getDisplay(page.displayName)
    await load()
})

async function load() {
    page.display = getDisplay(page.displayName)
    page.views = makeViews(clone(page.display.views))
}

function changeDisplay(displayName) {
    page.displayName = displayName
    page.display = getDisplay(displayName)
    page.views = makeViews(clone(page.display.views))
}

function makeViews(views, result = [], parent) {
    for (let view of views) {
        result.push(view)
        /*
        if (parent?.tabs) {
            view.tab = titlecase(view.tab || view.name || view.path)
        } */
        if (view.views && (view.path == '/' || page.showNested)) {
            makeViews(view.views, result, view)
        }
    }
    return result
}

async function clicked({action, item, items, column, rec, row}) {
    if (action == 'add') {
        if (!can('admin')) {
            return
        }
        editPage({})
    } else if (action == 'edit' || (action == 'cell' && column.name == 'edit')) {
        if (can('admin')) {
            editPage(item)
        }
    } else if (action == 'cell') {
        if (column.name == 'design') {
            if (item.component == 'Page' || item.component == 'Home') {
                State.ref.router
                    .push({name: 'page-editor', query: {path: item.path, design: true}})
                    .catch(() => {})
            }
        } else if (column.name == 'show') {
            go(item.path)
        }
    }
}

async function editPage(view) {
    if (view) {
        page.showPageEdit = true
        page.view = Object.assign({}, view)
    } else {
        page.showPageEdit = false
        page.view = null
    }
    await load()
    window.scrollTo({top: 0, behavior: 'smooth'})
}

function changeShowNested(value) {
    page.showNested = value
    State.app.showNestedPages = value
    page.views = makeViews(clone(page.display.views))
}

function accepts(el, target, source, sibling) {
    let index = findDomIndex(target, el)
    let targetIndex = findDomIndex(target, sibling)
    // Can't move or replace the first item
    if (index == 0 || targetIndex == 0) {
        Feedback.error('Cannot move or replace the home page')
        return false
    }
    if (!page.showNested) {
        return true
    }
    let display = page.display
    let {views} = getView(page.views[index].path, display)
    let {views: targetViews} = getView(page.views[targetIndex].path, display)
    if (views != targetViews) {
        // Moving to a different parent - reject the move
        Feedback.error('Cannot move a child view to a different parent')
        return false
    }
    return true
}

async function dragged(event) {
    let {type, index, targetIndex} = event.detail
    if (type == 'drop') {
        let display = clone(page.display)
        // Find the source and target views and ensure they have the same parent
        let {views, view, key} = getView(page.views[index].path, display)
        let {views: targetViews, key: targetKey} = getView(page.views[targetIndex].path, display)
        if (views != targetViews) {
            // Moving to a different parent - reject the move
            Feedback.error('Cannot move a child view to a different parent')
            return
        }
        views.splice(targetKey - 0, 0, ...views.splice(key - 0, 1))
        updateDisplay(display)
        await load()

        // May need reload of sidebar and other nav if menu order changes
        State.app.setNeed('reload')
        State.app.setNeedSave('display')
    } else if (type == 'remove') {
        // TODO?
    }
}

async function deploy() {
    Progress.run(async () => {
        await persistDisplay()
        State.app.noNeed('deploy')
    }, {page, field: 'deploying', feedback: 'App Deploying to CDN'})
}

function findDomIndex(container, el) {
    return Array.prototype.indexOf.call(container.children, el)
}
</script>

<template>
    <div class="view page page-list">
        <vu-help url="/doc/ui/view/view-list.html" />

        <SelectDisplay
            v-if="page.displays.length > 1"
            v-model="page.displayName"
            @update:modelValue="changeDisplay" />

        <vu-table
            name="pages"
            nodata=" "
            options="filter,toolbar"
            ref="table"
            row-key="path"
            width="100%"
            :data="page.views"
            :fields="page.fields"
            :select="page.select"
            :pageSize="State.app.mobile ? 14 : 200"
            :drag="page.drag"
            @click="clicked"
            @reload="load"
            @drag="dragged">
            <template v-slot:more="props">
                <v-btn v-if="State.app.needs.deploy" color="primary" class="mr-4" :loading="page.deploying" @click="deploy">Deploy</v-btn>
                <v-btn v-if="can('admin')" color="primary" class="mr-4" @click="editPage({})">
                    Add
                </v-btn>
                <vu-input
                    v-model="page.showNested"
                    suffix="Show Nested"
                    class="mr-3"
                    type="switch"
                    hide-details
                    :inset="false"
                    @update:modelValue="changeShowNested" />
            </template>
        </vu-table>

        <vu-panel v-model="page.showPageEdit" :widths="['700px']" @close="editPage">
            <PageEdit v-model="page.view" @close="editPage" />
        </vu-panel>
        <vu-confirm ref="confirm" />
    </div>
</template>

<style>
.page-list {
    margin-bottom: 40px;
    .table-data {
        i {
            color: rgb(var(--v-theme-primary)) !important;
        }
    }
    .vu-table {
        .v-btn {
            vertical-align: top;
        }
        .vu-input {
            display: inline-block;
            position: relative;
        }
    }
    .more {
        height: 36px;
    }
}
</style>
